import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import LockClockIcon from '@mui/icons-material/LockClock';
import { useEffect } from 'react';
import { sessionExpiredStore, useSessionExpired } from '@ota/api-client';

export interface SessionExpiredDialogProps {
  /**
   * App's auth store. {@code get()} is used to detect whether the user had
   * an active session (so we don't show "Session expired" to anonymous
   * users hitting public-page 401s). {@code clear()} is invoked when the
   * user acknowledges the dialog so the local cached user is wiped before
   * navigating to login.
   */
  authStore: { get(): unknown | null; clear: () => void };
  /** Login route. Default: '/login'. The user's pre-expiry path is appended as {@code ?returnTo=...}. */
  loginPath?: string;
  /** Header text. Default: "Session expired". */
  title?: string;
  /** Body copy. Defaults to a polite security-framed message. */
  message?: string;
  /** Action label. Default: "Sign in again". */
  confirmLabel?: string;
}

/**
 * Industry-standard session-expired modal. Mount once at the root of each
 * app — it subscribes to {@link sessionExpiredStore} and shows itself when
 * any API call (anywhere in the tree) returns 401.
 *
 * <h3>Design rules followed</h3>
 * <ul>
 *   <li><b>Coalesced</b>: multiple simultaneous 401s show ONE dialog
 *       (handled by the idempotent store, not by this component).</li>
 *   <li><b>Non-dismissable</b>: backdrop click + Escape are blocked. The
 *       only way out is the OK button. Anything else risks the user
 *       continuing to interact with stale state.</li>
 *   <li><b>returnTo</b>: the URL the user was on is captured at trigger
 *       time and round-tripped as {@code ?returnTo=...} so the login page
 *       can deep-link back after auth succeeds.</li>
 *   <li><b>Suppressed on login</b>: if the user is already on the login
 *       page (e.g. a stale 401 fires after they logged out), the dialog
 *       hides itself silently — no point telling them to log in when
 *       that's exactly what they're doing.</li>
 *   <li><b>Full reload on confirm</b>: {@code window.location.href} not
 *       in-router navigation, so any in-memory state from the expired
 *       session is wiped along with the auth store.</li>
 * </ul>
 */
export function SessionExpiredDialog({
  authStore,
  loginPath = '/login',
  title = 'Session expired',
  message = "For your security, you've been signed out due to inactivity. Please sign in again to continue.",
  confirmLabel = 'Sign in again',
}: SessionExpiredDialogProps) {
  const expired = useSessionExpired();

  // Silently skip if the user is already on the login page.
  const onLoginPage =
    typeof window !== 'undefined' && window.location.pathname.startsWith(loginPath);

  // Was there an active session at the time of the 401? If not, this is a
  // public-page 401 (e.g. /auth/me on Home for an anonymous user) and we
  // should NOT show a "session expired" dialog to someone who never had
  // a session. Auto-reset the store so future legitimate expirations can
  // still trigger it.
  const hadSession = !!authStore.get();
  useEffect(() => {
    if (expired && !hadSession) {
      sessionExpiredStore.reset();
    }
  }, [expired, hadSession]);

  const handleConfirm = () => {
    const returnTo = sessionExpiredStore.returnTo();
    authStore.clear();
    sessionExpiredStore.reset();
    const target =
      returnTo && returnTo !== '/' && !returnTo.startsWith(loginPath)
        ? `${loginPath}?returnTo=${encodeURIComponent(returnTo)}`
        : loginPath;
    // Full-page navigation — wipes any in-memory React state along with the
    // auth store, so the next session starts truly fresh.
    window.location.href = target;
  };

  return (
    <Dialog
      open={expired && !onLoginPage && hadSession}
      // Suppress all the dismissal paths — only the OK button exits.
      disableEscapeKeyDown
      onClose={(_event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
      }}
      maxWidth="xs"
      fullWidth
      // Make sure it floats above anything else (drawers, snackbars, etc.).
      sx={{ zIndex: (t) => t.zIndex.modal + 100 }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <LockClockIcon color="warning" />
          <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" color="text.secondary">
          {message}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="secondary"
          fullWidth
          autoFocus
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
